import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { t } from './i18n';
import { QButton } from './q/QButton';

// ─────────────────────────────────────────────────────────────────
// BOTTENMENY — flytande, frostad oval som Qvenaris (qvenari/apps/web/src/
// components/BottomNav.tsx), med Qvestiums rosa i stället för orange.
// Visas i appen och i smal webb; toppens flikrad göms då.
// ─────────────────────────────────────────────────────────────────

/** Ovalens höjd utan marginal och säkerhetsmarginal. */
export const BOTTOM_NAV_H = 60;
/** Avståndet från ovalen till skärmens nederkant (ovanför iPhones hemindikator). */
const BOTTOM_GAP = 10;
/** Utrymme sidorna lämnar längst ned så att inget hamnar under menyn. */
export const BOTTOM_NAV_SPACE = `calc(${BOTTOM_NAV_H + BOTTOM_GAP * 2}px + env(safe-area-inset-bottom, 0px))`;

const C = {
  pink: 'hsl(var(--primary))',
  text: '#111111',
  textSub: '#4a4a4a',
  textMuted: '#8f8a8c',
  border: '#e8e6e7',
};

/** Samma yta som Qvenaris jaktmeny: halvgenomskinlig, suddig bakgrund och mjuk skugga. */
const surface = (bg: string): CSSProperties => ({
  background: `color-mix(in srgb, ${bg} 82%, transparent)`,
  backdropFilter: 'blur(18px) saturate(1.6)',
  WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
  border: `1px solid ${C.border}`,
  borderRadius: 999,
  boxShadow: '0 8px 28px rgba(0,0,0,0.16), 0 1px 3px rgba(0,0,0,0.10)',
});

export type BottomKey = 'garderob' | 'marknad' | 'meddelanden';
/** Var användaren är; sidor under Mer markerar Mer. */
export type BottomActive = BottomKey | 'drops' | 'profil' | 'admin' | null;
const UNDER_MORE: BottomActive[] = ['drops', 'profil', 'admin'];

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const ICONS: Record<BottomKey | 'mer' | 'drops' | 'profil' | 'admin' | 'login' | 'logout', ReactNode> = {
  garderob: <><path d="M12 6.5a2 2 0 1 1 2-2" /><path d="M12 6.5v1.2L3.6 13.2A1.4 1.4 0 0 0 4.4 15.8h15.2a1.4 1.4 0 0 0 .8-2.6L12 7.7" /></>,
  marknad: <><path d="M6 7h12l1 13H5L6 7z" /><path d="M9 10V6a3 3 0 0 1 6 0v4" /></>,
  meddelanden: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.4A8 8 0 1 1 21 12z" />,
  mer: <><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>,
  drops: <><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>,
  profil: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  admin: <path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6z" />,
  login: <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5M15 12H3" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></>,
};
const Icon = ({ k, size = 22 }: { k: keyof typeof ICONS; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...P} aria-hidden="true">{ICONS[k]}</svg>
);

function Badge({ n, style }: { n: number; style?: CSSProperties }) {
  return (
    <span style={{ background: C.pink, color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px', minWidth: 15, textAlign: 'center', boxSizing: 'border-box', ...style }}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * Assistent (Q), Min garderob, Plagg & accessoarer, Meddelanden och Mer (drops, profil, admin,
 * språk, logga in/ut). Göms medan Q-panelen är öppen.
 */
export function BottomNav({
  active, onSelect, onQ, qOpen, unread, onDrops, onProfil, onAdmin, language, loggedIn, onLogin, onLogout,
}: {
  active: BottomActive;
  onSelect: (k: BottomKey) => void;
  /** Q: ett tryck öppnar panelen, ett långt tryck börjar lyssna direkt. */
  onQ: (listen: boolean) => void;
  qOpen?: boolean;
  /** Olästa meddelanden (röd bubbla på Meddelanden). */
  unread?: number;
  onDrops: () => void;
  onProfil: () => void;
  /** Bara för admin. */
  onAdmin?: () => void;
  /** Språkväljaren (LanguageSwitcher i App). */
  language: ReactNode;
  loggedIn: boolean;
  onLogin: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open]);

  const merActive = open || (!qOpen && UNDER_MORE.includes(active));
  const pick = (k: BottomKey) => { setOpen(false); onSelect(k); };
  const run = (f: () => void) => () => { setOpen(false); f(); };

  const item = (on: boolean): CSSProperties => ({
    // Bredd efter etiketten (Meddelanden är längst) plus lika stor del av resten.
    flex: '1 1 auto', minWidth: 52, height: BOTTOM_NAV_H - 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
    borderRadius: 999, border: `1.5px solid ${on ? C.pink : 'transparent'}`,
    background: on ? `color-mix(in srgb, ${C.pink} 14%, transparent)` : 'transparent',
    cursor: 'pointer', padding: '0 2px', fontFamily: 'inherit', boxSizing: 'border-box',
    color: on ? C.pink : C.text, fontSize: 10.5, fontWeight: on ? 700 : 600, letterSpacing: '0.01em',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s', WebkitTapHighlightColor: 'transparent',
  });
  const labelStyle: CSSProperties = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const row = (on = false): CSSProperties => ({
    width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', minHeight: 44, background: 'transparent',
    border: 'none', cursor: 'pointer', color: on ? C.pink : C.text, fontSize: 15, fontWeight: on ? 700 : 500, fontFamily: 'inherit', textAlign: 'left',
  });
  const heading: CSSProperties = { color: C.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '8px 18px 4px' };
  const divider = <div style={{ height: 1, background: C.border, margin: '6px 0' }} />;

  const tabs: { k: BottomKey; label: string; title?: string }[] = [
    { k: 'garderob', label: t('bottomNav.wardrobe'), title: t('allTabs.myWardrobe') },
    { k: 'marknad', label: t('bottomNav.market'), title: t('bottomNav.marketTitle') },
    { k: 'meddelanden', label: t('bottomNav.messages') },
  ];

  return (
    <>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1049, background: 'rgba(0,0,0,0.28)' }}>
          <div
            role="menu" aria-label={t('bottomNav.more')}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', right: 12, left: 12, marginLeft: 'auto', maxWidth: 320,
              bottom: `calc(${BOTTOM_NAV_H + BOTTOM_GAP + 8}px + env(safe-area-inset-bottom, 0px))`,
              maxHeight: `calc(100% - ${BOTTOM_NAV_H + BOTTOM_GAP + 24}px - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px))`, overflowY: 'auto',
              ...surface('#ffffff'), borderRadius: 18, padding: '6px 0', color: C.text,
            }}
          >
            <button role="menuitem" onClick={run(onDrops)} style={row(active === 'drops')}><Icon k="drops" size={20} />{t('allTabs.upcomingDrops')}</button>
            {divider}
            <div style={heading}>{t('bottomNav.myPages')}</div>
            <button role="menuitem" onClick={run(onProfil)} style={row(active === 'profil')}><Icon k="profil" size={20} />{t('app.nav.myProfile')}</button>
            {onAdmin && <button role="menuitem" onClick={run(onAdmin)} style={row(active === 'admin')}><Icon k="admin" size={20} />{t('adminTab.label')}</button>}
            <div style={{ ...row(), cursor: 'default', justifyContent: 'space-between' }}>
              <span style={{ color: C.textSub, fontSize: 14 }}>{t('bottomNav.language')}</span>{language}
            </div>
            {divider}
            {loggedIn
              ? <button role="menuitem" onClick={run(onLogout)} style={{ ...row(), color: C.textSub }}><Icon k="logout" size={20} />{t('app.nav.logOut')}</button>
              : <button role="menuitem" onClick={run(onLogin)} style={row()}><Icon k="login" size={20} />{t('common.logIn')}</button>}
          </div>
        </div>
      )}
      <nav
        aria-label={t('bottomNav.label')}
        style={{
          position: 'fixed', left: 12, right: 12, bottom: `calc(${BOTTOM_GAP}px + env(safe-area-inset-bottom, 0px))`, zIndex: 1050,
          maxWidth: 520, margin: '0 auto', height: BOTTOM_NAV_H, padding: '0 6px', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', gap: 2, ...surface('#ffffff'),
        }}
      >
        <QButton title={t('bottomNav.assistant')} onOpen={listen => { setOpen(false); onQ(listen); }} style={item(!!qOpen)}>
          <img src="/qvestium-logo.png" alt="" style={{ height: 22, width: 'auto', objectFit: 'contain' }} />
          <span style={labelStyle}>{t('bottomNav.assistant')}</span>
        </QButton>
        {tabs.map(({ k, label, title }) => {
          const on = active === k && !open && !qOpen;
          const badge = k === 'meddelanden' && unread ? unread : 0;
          return (
            <button key={k} type="button" onClick={() => pick(k)} title={title} aria-current={on ? 'page' : undefined} style={item(on)}
              aria-label={badge ? t('app.nav.messagesUnread', { count: badge }) : title}>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <Icon k={k} />
                {!!badge && <Badge n={badge} style={{ position: 'absolute', top: -6, left: '100%', marginLeft: -8 }} />}
              </span>
              <span style={labelStyle}>{label}</span>
            </button>
          );
        })}
        <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-haspopup="menu" style={item(merActive)}>
          <Icon k="mer" /><span style={labelStyle}>{t('bottomNav.more')}</span>
        </button>
      </nav>
    </>
  );
}
